/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { WorkflowGraphNode } from 'cio/utils/visual-workflow-builder/workflow-graph';
import characterFromNumber from 'cio/utils/character-from-number';
import { formatRandomCohortPathValue } from 'cio/utils/visual-workflow-builder/branching';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { deserializeFilter } from 'cio/utils/conditions/deserialize/attribute';
import ActionModel, { ExitType } from 'cio/models/action';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import { Periods } from 'cio/utils/metrics/common';
import { TimeRange } from 'cio/utils/time';
import { isEmpty } from '@ember/utils';
import { ParsedTreeBranchNode } from 'cio/models/campaign';
import {
  ConditionalBranchPathData,
  MultiSplitBranchPathData,
  PathType,
  RandomCohortBranchPathData,
  SegmentMultiSplitBranchPathData,
  WaitUntilBranchPathType,
  WaitUntilPathData,
} from 'cio/utils/visual-workflow-builder/branch-paths';

interface VisualWorkflowBuilderV2PathLabelArgs {
  node: WorkflowGraphNode;
  forceShowMetrics: boolean;
  highlightedNodeIds: string[];
  tab: VWBTab;
  metricsTabSelectedMeasures: string[];
  metricsTabTimePeriod: Periods;
  metricsTabTimeRange: TimeRange;
}

type WorkflowGraphNodeWithAction = WorkflowGraphNode & {
  branch: ParsedTreeBranchNode & {
    action: ActionModel;
  };
};

export default class VisualWorkflowBuilderV2PathLabel extends Component<VisualWorkflowBuilderV2PathLabelArgs> {
  @service store!: DS.Store;

  get isHighlighted() {
    if (!this.branchAction?.id) {
      return false;
    }

    return this.args.highlightedNodeIds.includes(this.branchAction.id);
  }

  get index() {
    if (this.args.node.type !== 'virtual_path_label') {
      return 0;
    }

    return this.args.node.index;
  }

  get branchAction() {
    return this.args.node.branch?.action;
  }

  get edgesCount() {
    let { branchAction } = this;

    if (branchAction == null) {
      return 0;
    }

    if (branchAction.type === 'conditional_branch_action') {
      return 2;
    } else if (branchAction.type === 'random_cohort_branch_action') {
      return branchAction.cohorts.length;
    } else if (branchAction.type === 'multi_split_branch_action') {
      // multiConditions don't define the "All others" path, so we add 1
      return branchAction.multiConditions.length + 1;
    } else if (branchAction.type === 'conditional_wait_action') {
      let pathsCount = 0;
      let multiConditionsCount = branchAction.multiConditions.length ?? 0;
      pathsCount += multiConditionsCount;

      if (!isEmpty(branchAction.delayEventAttr)) {
        pathsCount += 1;
      }

      if (branchAction.exitType !== ExitType.Never) {
        pathsCount += 1;
      }

      return pathsCount;
    }

    return 0;
  }

  private getPathLabelDataForConditionalBranchNode(
    node: WorkflowGraphNodeWithAction,
    index: number,
  ): ConditionalBranchPathData | null {
    let { branch } = node;
    let allNodesInPath: ActionModel[] = [];

    if (branch && branch.type === 'branch') {
      allNodesInPath = branch.edges[index].allEdgeNodes;
    }

    return {
      type: PathType.ConditionalPath,
      actionType: 'conditional_branch_action',
      allNodesInPath,
      label: index === 0 ? 'True' : 'False',
    };
  }

  private getPathLabelDataForRandomCohortBranchNode(
    node: WorkflowGraphNodeWithAction,
    index: number,
  ): RandomCohortBranchPathData | null {
    let { branch } = node;
    let { action } = branch;

    let { cohorts, isDistributedEvenly } = action;
    let cohort = cohorts[index];

    let allNodesInPath: ActionModel[] = [];

    if (branch && branch.type === 'branch') {
      allNodesInPath = branch.edges[index].allEdgeNodes;
    }

    return {
      type: PathType.RandomCohortPath,
      actionType: 'random_cohort_branch_action',
      allNodesInPath,
      label: `Path ${characterFromNumber(index + 1)}: ${
        isDistributedEvenly && cohort === 1
          ? formatRandomCohortPathValue(action.evenDistributionValue, true)
          : formatRandomCohortPathValue(cohort, true)
      }%`,
    };
  }

  private getPathLabelDataForMultiSplitBranchNode(
    node: WorkflowGraphNodeWithAction,
    index: number,
  ): MultiSplitBranchPathData | null {
    let { branch } = node;
    let { action } = branch;

    let allNodesInPath: ActionModel[] = [];

    if (branch && branch.type === 'branch') {
      allNodesInPath = branch.edges[index].allEdgeNodes;
    }

    if (index === this.edgesCount - 1) {
      // "All others" edge
      return {
        actionType: 'multi_split_branch_action',
        allNodesInPath,
        type: PathType.AllOthersMultiSplitPath,
        label: 'All others',
      };
    }

    let correspondingFilter = action.multiConditions[index] as any;

    if (correspondingFilter != null && correspondingFilter.length > 0) {
      // In multi splits, there's only ever one filter in the individual
      // condition item
      let [correspondingCondition] = correspondingFilter;

      if (correspondingCondition.type === 'segments_negatable' || correspondingCondition.type === 'segments') {
        let edge: SegmentMultiSplitBranchPathData = {
          actionType: 'multi_split_branch_action',
          allNodesInPath,
          type: PathType.SegmentMultiSplitPath,
          icon: '',
          label: '',
          segmentId: '',
        };
        if (correspondingCondition.segments.length > 0) {
          let [segmentId] = correspondingCondition.segments;
          let segment = this.store.peekRecord('short-segment', segmentId);

          if (segment) {
            edge.label = segment.name;
            edge.icon = segment.icon;
            edge.segmentId = segmentId;
          }
        }

        return edge;
      } else {
        return {
          actionType: 'multi_split_branch_action',
          allNodesInPath,
          type: PathType.AttributeMultiSplitPath,
          label: correspondingCondition.value,
          condition: deserializeFilter(correspondingCondition),
        };
      }
    }

    return {
      actionType: 'multi_split_branch_action',
      allNodesInPath,
      type: PathType.InactiveMultiSplitPath,
      label: 'Inactive: no value added',
    };
  }

  private getPathLabelDataForWaitUntilBranchNode(
    node: WorkflowGraphNodeWithAction,
    index: number,
  ): WaitUntilPathData | null {
    let { branch } = node;
    let { action } = branch;
    let allNodesInPath: ActionModel[] = [];

    if (branch && branch.type === 'branch') {
      allNodesInPath = branch.edges[index].allEdgeNodes;
    }

    let pathTypes: WaitUntilBranchPathType[] = action.multiConditions.map(() => PathType.ConditionWaitUntilPath);

    if (!isEmpty(action.delayEventAttr)) {
      pathTypes.push(PathType.DelayEventWaitUntilPath);
    }

    if (action.exitType !== ExitType.Never) {
      pathTypes.push(PathType.MaximumWaitUntilPath);
    }

    let typeOfCurrentPath = pathTypes[index];

    let base = {
      actionType: 'conditional_wait_action' as const,
      allNodesInPath,
    };

    switch (typeOfCurrentPath) {
      case PathType.ConditionWaitUntilPath:
        return {
          ...base,
          type: typeOfCurrentPath,
          label: 'Condition met',
          icon: 'trigger-segment',
          conditions: action.multiConditions[index] ?? [],
        };
      case PathType.DelayEventWaitUntilPath:
        return {
          ...base,
          type: typeOfCurrentPath,
          label: 'Event time',
          icon: 'event',
          delayEventAttrOffset: action.delayEventAttrOffset,
          delayEventAttr: action.delayEventAttr,
        };
      case PathType.MaximumWaitUntilPath:
        return {
          ...base,
          type: typeOfCurrentPath,
          label: 'Maximum wait',
          icon: 'time-window',
          exitType: action.exitType,
          delay: action.delay,
        };
    }
  }

  get edge() {
    let { node } = this.args;
    let { branch } = this.args.node;

    if (branch == null) {
      return null;
    }

    let { action } = branch;

    if (action == null) {
      return null;
    }

    if (action.type === 'conditional_branch_action') {
      return this.getPathLabelDataForConditionalBranchNode(node as WorkflowGraphNodeWithAction, this.index);
    } else if (action.type === 'random_cohort_branch_action') {
      return this.getPathLabelDataForRandomCohortBranchNode(node as WorkflowGraphNodeWithAction, this.index);
    } else if (action.type === 'multi_split_branch_action') {
      return this.getPathLabelDataForMultiSplitBranchNode(node as WorkflowGraphNodeWithAction, this.index);
    } else if (action.type === 'conditional_wait_action') {
      return this.getPathLabelDataForWaitUntilBranchNode(node as WorkflowGraphNodeWithAction, this.index);
    }

    return null;
  }
}
